import React, { useState } from 'react';
import { 
    Box, 
    Tabs, 
    Tab, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Typography 
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { LOCALE_STRING } from '../../common/utility/constant';
import usePermissions from '../../common/hooks/usePermissions';

const getNumberStyle = (value) => ({
    color: value > 0 ? '#4caf50' : value < 0 ? '#f44336' : 'inherit',
    fontWeight: 'bold'
});

const getColorForTitle = (title) => {
    if (title === 'FII' || title === 'DII') return '#fff4bd';
    return '#ffc552';
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: 'rgba(65,94,129,0.18)', // subtle blue-grey highlight
    },
    '&:hover .MuiTableCell-root': {
        backgroundColor: 'rgba(65,94,129,0.18) !important', // force cell highlight
    },
}));

const TabPanel = styled(Box)(({ theme }) => ({
    minWidth: '100%',
    width: '100%',
    width: '100%',
    marginTop: '1rem',
    '& .MuiTableHead-root .MuiTableCell-root': {
        backgroundColor: '#022D5A !important',
        color: '#fff',
        fontWeight: 'bold'
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
        backgroundColor: '#001E3C',
        color: '#fff'
    },
    '& .MuiTable-root': {
        border: '2px solid #415e81',
        borderCollapse: 'collapse',
        borderRadius: '4px',
        overflow: 'hidden',
    }
}));

const StyledTableCell = styled(TableCell)(({ theme, header, isFirstInCategory }) => ({
    fontWeight: 'bold',
    border: '1px solid #415e81',
    borderColor: '#415e81',
    padding: '6px 8px',
    backgroundClip: 'padding-box',
    backgroundColor: header ? '#022D5A' : '#001E3C',
    color: '#fff',
    borderLeft: isFirstInCategory ? '2px solid #90caf9' : '1px solid #415e81',
    '&.category-header': {
        backgroundColor: '#001E3C',
        fontSize: '1.1rem',
        borderBottom: 'none',
    }
}));

const PositionSeriesTable = ({ seriesSections, dateRange }) => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState('FII');
    const permissions = usePermissions();
    const isAdminUser = permissions.isAdmin();
    // For testing 'No Data' state - uncomment to test
    // const [testNoData, setTestNoData] = useState(false);
    // const testDateRange = testNoData ? [] : dateRange;
    // const testSeriesSections = testNoData ? {} : seriesSections;
    
    // Removed redundant helper functions for role names as they're handled in parent component

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Column headers for the table with categories
    let columnCategories = [
        {
            name: 'Futures',
            columns: [
                { id: 'long_doi', label: 'Long' },
                { id: 'short_doi', label: 'Short' },
                { id: 'net', label: 'Net' },
            ]
        },
        {
            name: 'Call Options',
            columns: [
                { id: 'ce_long_doi', label: 'Long' },
                { id: 'ce_short_doi', label: 'Short' },
                { id: 'ce_net', label: 'Net' },
            ]
        },
        {
            name: 'Put Options',
            columns: [
                { id: 'pe_long_doi', label: 'Long' },
                { id: 'pe_short_doi', label: 'Short' },
                { id: 'pe_net', label: 'Net' },
            ]
        }
    ];
    if(isAdminUser){
        columnCategories = [
            {
                name: 'Nifty',
                columns: [
                    { id: 'vwap', label: 'VWAP' },
                    { id: 'vpoc', label: 'VPOC' },
                    { id: 'close', label: 'Close' },
                ]
            },
            {
                name: 'Futures',
                columns: [
                    { id: 'long_doi', label: 'Long' },
                    { id: 'short_doi', label: 'Short' },
                    { id: 'net', label: 'Net' },
                ]
            },
            {
                name: 'Call Options',
                columns: [
                    { id: 'ce_long_doi', label: 'Long' },
                    { id: 'ce_short_doi', label: 'Short' },
                    { id: 'ce_net', label: 'Net' },
                ]
            },
            {
                name: 'Put Options',
                columns: [
                    { id: 'pe_long_doi', label: 'Long' },
                    { id: 'pe_short_doi', label: 'Short' },
                    { id: 'pe_net', label: 'Net' },
                ]
            }
        ];
    }
    

    // Flatten columns for data handling
    const columns = [
        { id: 'date', label: 'Date' },
        ...columnCategories.flatMap(category => category.columns)
    ];

    const getNumberStyle = (value) => ({
        color: value > 0 ? '#4caf50' : value < 0 ? '#f44336' : '#fff',
        fontWeight: value !== 0 ? 'bold' : 'normal'
    });

    const formatValue = (value) => {
        return typeof value === 'number' 
            ? Number(value).toLocaleString(LOCALE_STRING)
            : value;
    };

    /**
     * Transforms raw series data into row data for the table
     * For each date, we:
     * 1. Get the participant data (FII/PRO/CLIENT/DII) for that date
     * 2. Extract the base metrics (long_doi, short_doi)
     * 3. Calculate net positions:
     *    - Futures net = long_doi - short_doi
     *    - CE net = ce_long_doi - ce_short_doi
     *    - PE net = pe_long_doi - pe_short_doi
     * 4. Handle missing data with null (?? 0)
     */
    const getRowData = (date) => {
        // Get participant data for the selected date and tab (FII/PRO/CLIENT/DII)
        const data = seriesSections[date]?.[activeTab] ?? {};
        
        return {
            date,
            // Nifty data
            vwap: data.vwap ?? 0,
            vpoc: data.vpoc ?? 0,
            close: (data.close ?? 0),

            // Futures positions
            long_doi: data.long_doi ?? 0,
            short_doi: data.short_doi ?? 0,
            net: (data.long_doi ?? 0) - (data.short_doi ?? 0),
            
            // Call options positions
            ce_long_doi: data.ce_long_doi ?? 0,
            ce_short_doi: data.ce_short_doi ?? 0,
            ce_net: (data.ce_long_doi ?? 0) - (data.ce_short_doi ?? 0),
            
            // Put options positions
            pe_long_doi: data.pe_long_doi ?? 0,
            pe_short_doi: data.pe_short_doi ?? 0,
            pe_net: (data.pe_long_doi ?? 0) - (data.pe_short_doi ?? 0)
        };
    };

    return (
        <Box sx={{
                                                             width: '100%' }}>
            {/* Test Controls - uncomment to test "No Data Available" message
            <Box sx={{
                                                             mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                    onClick={() => setTestNoData(!testNoData)}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: testNoData ? '#f44336' : '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                    }}
                >
                    {testNoData ? 'Show Data' : 'Test No Data'}
                </button>
            </Box>
            */}
            <Box sx={{borderBottom: 1, borderColor: '#415e81', mb: 2 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    variant="fullWidth"
                >
                    {['FII', 'PRO', 'CLIENT', 'DII'].map((tab) => (
                        <Tab 
                            key={tab}
                            label={tab} 
                            value={tab}
                            sx={{
                                                            
                                backgroundColor: '#415e81',
                                color: '#fff',
                                margin: '0 4px',
                                '&.Mui-selected': {
                                    backgroundColor: '#022D5A',
                                    color: '#fff',
                                    opacity: 1
                                },
                                '&:hover': {
                                    backgroundColor: '#1a4976',
                                    opacity: 1
                                }
                            }}
                        />
                    ))}
                </Tabs>
            </Box>
            {/* Debug panel moved to parent component */}
            
            <Box sx={{
                position: 'relative',
                width: '100%',
                mt: 2,
                overflow: 'hidden'
            }}>
            <TableContainer 
                component={Paper} 
                sx={{
                    backgroundColor: 'transparent',
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent'
                    },
                    width: '100%',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        height: '8px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#415e81',
                        borderRadius: '4px'
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#001E3C'
                    }
                }}
            >
                <Table sx={{ 
                    width: '100%', 
                    tableLayout: 'auto',
                    '& .MuiTableCell-root': {
                        whiteSpace: 'nowrap'
                    }
                }}>
                    <TableHead>
                        {/* Category Headers */}
                        <TableRow>
                            <StyledTableCell 
                                header={true}
                                align="left"
                                sx={{ 
                                    backgroundColor: '#022D5A !important',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2
                                }}
                            />
                            {columnCategories.map((category) => (
                                <StyledTableCell 
                                    key={category.name}
                                    colSpan={3}
                                    align="center"
                                    header={true}
                                    className="category-header"
                                    isFirstInCategory={true}
                                    sx={{
                                        backgroundColor: '#022D5A !important',
                                        borderBottom: 'none',
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                        color: '#90caf9',
                                        textAlign: 'center'
                                    }}
                                >
                                    {category.name}
                                </StyledTableCell>
                            ))}
                        </TableRow>
                        {/* Column Headers */}
                        <TableRow>
                            <StyledTableCell 
                                header={true}
                                align="left"
                                sx={{ 
                                    backgroundColor: '#022D5A !important',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2,
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                Date
                            </StyledTableCell>
                            {columnCategories.map((category) => 
                                category.columns.map((column) => (
                                    <StyledTableCell 
                                        key={column.id} 
                                        align="center"
                                        header={true}
                                        sx={{ 
                                            backgroundColor: '#022D5A !important',
                                            textAlign: 'center'
                                        }}
                                        isFirstInCategory={column.id.includes('vwap') || 
                                                            column.id.includes('long_doi') || 
                                                             column.id.includes('ce_long_doi') || 
                                                             column.id.includes('pe_long_doi')}
                                    >
                                        {column.label}
                                    </StyledTableCell>
                                ))
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(!dateRange?.length || !Object.keys(seriesSections || {}).length) ? (
                            <TableRow>
                                <StyledTableCell 
                                    colSpan={columns.length} 
                                    align="right"
                                    header={true}
                                    sx={{
                                        backgroundColor: '#022D5A !important',
                                        padding: '20px 0'
                                    }}
                                >
                                    No Data Available
                                </StyledTableCell>
                            </TableRow>
                        ) : (
                            <>
                                {dateRange.map((date) => {
                                    const row = getRowData(date);
                                    return (
                                        <StyledTableRow key={date}>
                                            <StyledTableCell 
                                                align="left"
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    zIndex: 1,
                                                    backgroundColor: '#001E3C !important',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {formatValue(row.date)}
                                            </StyledTableCell>
                                            {columnCategories.map(category => 
                                                category.columns.map((column) => (
                                                    <StyledTableCell 
                                                        key={column.id} 
                                                        align="right"
                                                        isFirstInCategory={column.id.includes('vwap') || 
                                                                            column.id.includes('long_doi') || 
                                                                             column.id.includes('ce_long_doi') || 
                                                                             column.id.includes('pe_long_doi')}
                                                        sx={{
                                                            ...getNumberStyle(row[column.id]),
                                                            borderLeft: (column.id.includes('vwap') || 
                                                                        column.id.includes('long_doi') || 
                                                                        column.id.includes('ce_long_doi') || 
                                                                        column.id.includes('pe_long_doi')) 
                                                                        ? '2px solid #90caf9' : '1px solid #415e81',
                                                            textAlign: 'right',
                                                            paddingRight: '16px'
                                                        }}
                                                    >
                                                        {formatValue(row[column.id])}
                                                    </StyledTableCell>
                                                ))
                                            )}
                                        </StyledTableRow>
                                    );
                                })}
                                {/* Total Row */}
                                <TableRow>
                                    <StyledTableCell 
                                        header={true} 
                                        sx={{ 
                                            backgroundColor: '#022D5A !important',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 2,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >Total</StyledTableCell>
                                    {columnCategories.map(category => 
                                        category.columns.map((column) => {
                                            const total = dateRange.reduce((sum, date) => {
                                                const row = getRowData(date);
                                                const value = row[column.id];
                                                return sum + (typeof value === 'number' ? value : 0);
                                            }, 0);
                                            return (
                                                <StyledTableCell 
                                                    key={column.id} 
                                                    align="right" 
                                                    header={true}
                                                    isFirstInCategory={column.id.includes('long_doi') || 
                                                                         column.id.includes('ce_long_doi') || 
                                                                         column.id.includes('pe_long_doi')}
                                                    sx={{
                                                        backgroundColor: '#022D5A !important',
                                                        ...getNumberStyle(total),
                                                        paddingRight: '16px',
                                                        borderLeft: (column.id.includes('long_doi') || 
                                                                    column.id.includes('ce_long_doi') || 
                                                                    column.id.includes('pe_long_doi')) 
                                                                    ? '2px solid #90caf9' : '1px solid #415e81'
                                                    }}
                                                >
                                                    {formatValue(total)}
                                                </StyledTableCell>
                                            );
                                        })
                                    )}
                                </TableRow>
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            </Box>
        </Box>
    );
};

export default PositionSeriesTable;
